/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

// Component imports
import { Button, Col, Popconfirm, Row, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { BarChartOutlined, EyeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import BroadcastForm from '../Forms/BroadcastForm';
import Permissioned from '../Permissioned';
import ReactMarkdown from 'react-markdown';
import SideModal from '../Layout/SideModal';
import { EditIcon } from '../Icons/EditIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import { BROADCAST_STATUSES, PERMISSIONS_GROUP_EDITOR, DEFAULT_DATETIME_FORMAT, DEFAULT_PAGINATION_SIZE } from '../../constants/main';
import { useLazyDownloadBroadcastAnalyticsQuery, useDeleteBroadcastMutation, useFetchBroadcastsQuery } from '../../store/broadcasts/broadcastsApi';
import { revertBlobTypes } from '../../store/utils/revertBlobTypes';
import { truncateMarkdown } from '../../utils/string/truncateMarkdown';
import is from '../../utils/is';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import handleFileDownload from '../../utils/misc/handleFileDownload';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import getSupportedPlatforms from '../../utils/misc/getSupportedPlatforms';

// Constant declarations
export const FORM_MODES = {
  CREATE: 'create',
  EDIT: 'edit',
  VIEW: 'view'
};

const BroadcastsTable = () => {
  const { tenantId, orgId } = useParams();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  const { data: broadcasts, isFetching: isLoadingBroadcasts } = useFetchBroadcastsQuery({
    orgId,
    tenantId,
    urlParams: {
      pageSize: pageSize,
      page: searchParams.get('page') || 0,
      sortBy: searchParams.get('sortBy') || 'sendAt',
      order: searchParams.get('order') || 'DESC'
    } }, { skip: !tenantId });
  const { data: botDeployments } = getCurrentQueryState('fetchBotDeployments', { orgId, tenantId });
  const [triggerDownloadBroadcastAnalytics, { isLoading: isLoadingBroadcastAnalytics }] = useLazyDownloadBroadcastAnalyticsQuery();
  const [deleteBroadcast, { isLoading: isDeletingBroadcast }] = useDeleteBroadcastMutation();
  const { handleMouseDown, isDragging } = useDragDetection();

  const formMode = !selectedBroadcast ? FORM_MODES.CREATE : selectedBroadcast?.status === BROADCAST_STATUSES.COMPLETE ? FORM_MODES.VIEW : FORM_MODES.EDIT;
  const availablePlatforms = useMemo(() => getSupportedPlatforms(botDeployments), [botDeployments]);

  const sortingInfo = useMemo(() => {
    let order = searchParams.get('order') ? (searchParams.get('order') === 'DESC' ? 'descend' : 'ascend') : 'descend';
    let columnKey = searchParams.get('sortBy') ? searchParams.get('sortBy') : 'sendAt';

    return {
      order,
      columnKey
    };
  }, [searchParams.get('order'), searchParams.get('sortBy')]);

  // On mount
  useEffect(() => {
    updateUrlSearchParams(Number(searchParams.get('page')) + 1, DEFAULT_PAGINATION_SIZE, { [sortingInfo.columnKey]: sortingInfo.order === 'descend' ? 'DESC' : 'ASC' });
  }, [tenantId]);

  const columns = [
    {
      title: t('Created by'),
      key: 'userFullName',
      responsive: ['xxl'],
      render: record => {
        return record.userFullName ? (
          <Tooltip
            overlayClassName="pointer-events-none"
            placement="topLeft"
            title={record.userEmail}
          >
            {record.userFullName}
          </Tooltip>
        ) : (
          <span className="text-secondary">
            {t('(user unknown)')}
          </span>
        );
      }
    },
    {
      title: t('Type'),
      key: 'type',
      dataIndex: 'type',
      responsive: ['xxl'],
      render: type => (
        <span>
          <Tag color="processing">
            {type}
          </Tag>
        </span>
      )
    },
    {
      title: t('Status'),
      key: 'status',
      sorter: true,
      defaultSortOrder: sortingInfo.columnKey === 'status' && sortingInfo.order,
      render: record => {
        switch (record.status) {
          case BROADCAST_STATUSES.SENDING:
          case BROADCAST_STATUSES.WAITING:
            return (
              <Fragment>
                <Tag color="processing">
                  {t('PROCESSING')}
                </Tag>

                {
                  record.currentBatch && record.totalBatches ? (
                    <Tooltip
                      overlayClassName="pointer-events-none"
                      placement="top"
                      title={`${record.currentBatch}/${record.totalBatches} batches`}
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  ) : null
                }
              </Fragment>
            );

          case BROADCAST_STATUSES.COMPLETE:
            return (
              <Tag color="success">
                {record.status}
              </Tag>
            );

          case BROADCAST_STATUSES.ERROR:
            return (
              <Tag color="error">
                {record.status}
              </Tag>
            );

          case BROADCAST_STATUSES.PENDING:
            return (
              <Tag color="warning">
                {record.status}
              </Tag>
            );

          default:
            return (
              <Tag color="processing">
                {record.status}
              </Tag>
            );
        }
      }
    },
    {
      title: t('Title'),
      dataIndex: 'title',
      key: 'title'
    },
    {
      title: t('Text'),
      dataIndex: 'text',
      key: 'text',
      responsive: ['lg'],
      render: record => (
        <Typography.Text>
          <ReactMarkdown
            skipHtml
            linkTarget="_blank"
          >
            {
              truncateMarkdown(record, {
                limit: 40,
                ellipsis: true,
                replaceImages: true,
                replaceBlankSpaces: true
              })
            }
          </ReactMarkdown>
        </Typography.Text>
      )
    },
    {
      title: t('Send at'),
      dataIndex: 'sendAt',
      key: 'sendAt',
      responsive: ['xl'],
      sorter: true,
      defaultSortOrder: sortingInfo.columnKey === 'sendAt' && sortingInfo.order,
      render: sendAt => (
        moment(sendAt).format(DEFAULT_DATETIME_FORMAT)
      )
    },
    {
      title: t('Audiences'),
      key: 'audiences',
      responsive: ['xxl'],
      render: record => {
        return record.groups && is.array(record.groups) ? (
          <span>
            {
              record.groups.map(group => {
                return (
                  <Tag key={group.id} className="m-1">
                    {group.label}
                  </Tag>
                );
              })
            }
          </span>
        ) : (
          <span className="text-secondary">
            {t('(No Audience info)')}
          </span>
        );
      }
    },
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'right',
      width: 140,
      align: 'center',
      render: record => (
        <Fragment>
          <Space onClick={(e) => e.stopPropagation()}>
            <Permissioned
              allowedPermissions={PERMISSIONS_GROUP_EDITOR}
              currentPermissions={userPermissions}
              fallbackComponent={null}
            >

              {
                record.status === BROADCAST_STATUSES.COMPLETE ? (
                  <Tooltip overlayClassName="pointer-events-none" title={t('View completed broadcast')}>
                    <Button
                      type="text"
                      shape="circle"
                      icon={<EyeOutlined />}
                      onClick={() => {
                        setSelectedBroadcast(record);
                        setIsModalOpen(true);
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Fragment>
                    <Tooltip overlayClassName="pointer-events-none" title={t('Edit Broadcast')}>
                      <Button
                        type="text"
                        shape="circle"
                        icon={<EditIcon />}
                        onClick={() => {
                          setSelectedBroadcast(record);
                          setIsModalOpen(true);
                        }}
                      />
                    </Tooltip>
                  </Fragment>
                )
              }

              <Popconfirm
                title={t('Are you sure you want to delete this broadcast?')}
                okText={t('Yes')}
                cancelText={t('No')}
                placement="topRight"
                onConfirm={() => {
                  onBroadcastDelete(record.id);
                }}
              >
                <Tooltip overlayClassName="pointer-events-none" title={t('Delete Broadcast')}>
                  <Button
                    type="text"
                    shape="circle"
                    icon={<DeleteIcon />}
                    loading={isDeletingBroadcast}
                    className="ml-2"
                  />
                </Tooltip>
              </Popconfirm>
            </Permissioned>

            <Tooltip overlayClassName="pointer-events-none" title={t('Download Analytics')}>
              <Button
                type="text"
                shape="circle"
                icon={<BarChartOutlined />}
                loading={isLoadingBroadcastAnalytics}
                onClick={() => onBroadcastAnalyticsDownload(record.id)}
                disabled={[BROADCAST_STATUSES.WAITING, BROADCAST_STATUSES.SENDING, BROADCAST_STATUSES.PENDING].includes(record.status)}
              />
            </Tooltip>
          </Space>
        </Fragment>
      )
    }
  ];

  const onBroadcastDelete = async (broadcastId) => {
    const isDeleted = await deleteBroadcast({ orgId, tenantId, broadcastId }).unwrap();

    // If delete was successful, refresh the list with the latest data
    if (isDeleted) {
      updateUrlSearchParams();
    }
  };

  const handleTableChange = async (pagination, _filters, sorter) => {
    let sorting;

    if (sorter.column) {
      sorting = { [sorter.columnKey]: sorter.order === 'ascend' ? 'ASC' : 'DESC' };
    }
    updateUrlSearchParams(pagination.current, pagination.pageSize, sorting);
  };

  const onBroadcastAnalyticsDownload = async (broadcastId) => {
    const result = await triggerDownloadBroadcastAnalytics({ orgId, tenantId, broadcastId }).unwrap();
    const file = revertBlobTypes(result, 'text/csv');
    const fileName = file?.headers['content-disposition']?.split(new RegExp(['"'], 'g'))?.[1] ?? `broadcast_${broadcastId}.csv`;
    handleFileDownload(file?.blob, fileName);
  };

  const updateUrlSearchParams = async (current = 1, pageSize = DEFAULT_PAGINATION_SIZE, sorting = { sendAt: 'DESC' }) => {
    setCurrentPage(current);
    setPageSize(pageSize);
    const urlSearchParams = new URLSearchParams(location.search);
    urlSearchParams.set('page', current - 1);

    if (sorting) {
      urlSearchParams.set('sortBy', Object.entries(sorting).flat()[0]);
      urlSearchParams.set('order', Object.entries(sorting).flat()[1]);
    } else {
      urlSearchParams.delete('sortBy');
      urlSearchParams.delete('order');
    }

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  return (
    <Fragment>
      <Permissioned
        allowedPermissions={PERMISSIONS_GROUP_EDITOR}
        currentPermissions={userPermissions}
        fallbackComponent={null}
      >
        <Row gutter={24} className="mb-4">
          <Col span={24}>
            <Tooltip
              title={
                availablePlatforms.length === 0
                  ? t('There are no platforms available to schedule a broadcast. Please create an agent deployment first.')
                  : ''
              }
            >
              <Button
                size="large"
                type="primary"
                icon={<PlusIcon />}
                className="float-right"
                disabled={availablePlatforms.length === 0}
                onClick={() => {
                  setSelectedBroadcast(null);
                  setIsModalOpen(true);
                }}
              >
                {t('Schedule Broadcast')}
              </Button>
            </Tooltip>
          </Col>
        </Row>
      </Permissioned>

      <Row gutter={24}>
        <Col xs={24}>
          <Table
            onRow={(record) => ({
              onMouseDown: handleMouseDown,
              onClick: () => {
                if(!isDragging() && userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission))) {
                  setSelectedBroadcast(record);
                  setIsModalOpen(true);
                }
              }
            })}
            rowClassName={(record) => record.status === BROADCAST_STATUSES.COMPLETE ? '' : 'cursor-pointer'}
            size="small"
            onChange={handleTableChange}
            rowKey="id"
            columns={columns}
            dataSource={broadcasts && is.array(broadcasts.items) && broadcasts.items}
            loading={isLoadingBroadcasts}
            pagination={{
              size: 'default',
              total: broadcasts?.total,
              defaultCurrent: 1,
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              showTotal: (total) => `${t('Total Broadcasts')}: ${total}`
            }}
          />
        </Col>
      </Row>

      <SideModal
        title={
          formMode === FORM_MODES.CREATE
            ? t('Schedule New Broadcast')
            : formMode === FORM_MODES.EDIT
              ? t('Edit Broadcast')
              : t('View Completed Broadcast')
        }
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        destroyOnClose
        bodyClassName="p-0"
      >
        <BroadcastForm
          canEdit={formMode !== FORM_MODES.VIEW}
          initialBroadcast={selectedBroadcast}
          bodyClassName="px-6 pt-6 flex-grow-1"
          footerClassName="ant-drawer-footer"
          onCancel={() => setIsModalOpen(false)}
          onSubmitSuccess={() => {
            if (formMode === FORM_MODES.CREATE) {
              setIsModalOpen(false);
            }
          }}
        />
      </SideModal>
    </Fragment>
  );
};

export default BroadcastsTable;
